import { useState, useEffect } from "react";

// ─── Config ─────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const SESSION_KEY = "hirrd_admin_key";
const READ_KEY = "hirrd_admin_read_contacts";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function adminFetch(path, key) {
  return fetch(`${API}${path}`, {
    headers: { "x-admin-key": key, "Content-Type": "application/json" },
  });
}
function adminDelete(path, key) {
  return fetch(`${API}${path}`, { method: "DELETE", headers: { "x-admin-key": key } });
}
function adminPost(path, key, body) {
  return fetch(`${API}${path}`, {
    method: "POST",
    headers: { "x-admin-key": key, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
function getReadSet() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
}
function markRead(id) {
  const s = getReadSet(); s.add(id);
  localStorage.setItem(READ_KEY, JSON.stringify([...s]));
}

// ─── Mobile hook ─────────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function ScoreBadge({ value, label }) {
  const color = value >= 80 ? "#22c55e" : value >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", border: `3px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", color, fontWeight: 700, fontSize: 12, background: `${color}18` }}>
        {Math.round(value)}
      </div>
      <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3 }}>{label}</div>
    </div>
  );
}

function Tag({ text }) {
  return (
    <span style={{ background: "rgba(139,92,246,0.15)", color: "#a78bfa", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>
      {text}
    </span>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 16, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 4px 24px rgba(0,0,0,0.4)" }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
        <div style={{ color: "#f0f0ff", fontSize: 28, fontWeight: 700, marginTop: 2, lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 140, maxWidth: 360 }}>
      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7280", fontSize: 15 }}>🔍</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 10, padding: "10px 14px 10px 36px", color: "#e0e0f0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = "#7c3aed"}
        onBlur={e => e.target.style.borderColor = "#1e1e2e"}
      />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #1e1e2e", borderTopColor: "#7c3aed", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#4b5563" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15 }}>{text}</div>
    </div>
  );
}

function InfoField({ label, value, isLink }) {
  return (
    <div>
      <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      {isLink && value ? (
        <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" style={{ color: "#7c3aed", fontSize: 14 }}>{value}</a>
      ) : (
        <div style={{ color: "#d1d5db", fontSize: 14 }}>{value || "—"}</div>
      )}
    </div>
  );
}

function ExtLink({ href, label }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>{label} ↗</a>;
}

// ─── Confirm Delete Modal ────────────────────────────────────────────────────
function ConfirmModal({ name, onConfirm, onCancel, loading }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#111118", border: "1px solid #2a2a40", borderRadius: 20, padding: "28px 24px", maxWidth: 400, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }}>
        <div style={{ fontSize: 32, textAlign: "center", marginBottom: 16 }}>🗑️</div>
        <div style={{ color: "#e0e0f0", fontWeight: 700, fontSize: 18, textAlign: "center", marginBottom: 8 }}>Delete {name}?</div>
        <div style={{ color: "#9ca3af", fontSize: 14, textAlign: "center", lineHeight: 1.6, marginBottom: 24 }}>
          This will permanently remove this entry and all associated data from the database. This cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onCancel} disabled={loading} style={{ flex: 1, padding: "11px", background: "none", border: "1px solid #1e1e2e", borderRadius: 10, color: "#9ca3af", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, padding: "11px", background: loading ? "#7f1d1d" : "linear-gradient(135deg,#dc2626,#ef4444)", border: "none", borderRadius: 10, color: "white", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Form Modal Wrapper ───────────────────────────────────────────────────────
function FormModal({ title, icon, onClose, onSubmit, loading, error, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#111118", border: "1px solid #2a2a40", borderRadius: 20, padding: "24px 20px", maxWidth: 540, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.7)", margin: "20px auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>{icon}</span>
            <span style={{ color: "#e0e0f0", fontWeight: 700, fontSize: 18 }}>{title}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>
        <form onSubmit={onSubmit}>
          <div style={{ display: "grid", gap: 14 }}>{children}</div>
          {error && (
            <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, color: "#f87171", fontSize: 13 }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: "11px", background: "none", border: "1px solid #1e1e2e", borderRadius: 10, color: "#9ca3af", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: "11px", background: loading ? "#3b2f63" : "linear-gradient(135deg,#7c3aed,#a855f7)", border: "none", borderRadius: 10, color: "white", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FInput({ label, required, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label style={{ color: "#9ca3af", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ""} required={required}
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        style={{ width: "100%", background: "#0d0d16", border: "1px solid #1e1e2e", borderRadius: 8, padding: "10px 14px", color: "#e0e0f0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = "#7c3aed"}
        onBlur={e => e.target.style.borderColor = "#1e1e2e"}
      />
    </div>
  );
}

function FTextarea({ label, required, value, onChange, placeholder, rows = 3 }) {
  return (
    <div>
      <label style={{ color: "#9ca3af", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      <textarea
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ""} required={required} rows={rows}
        style={{ width: "100%", background: "#0d0d16", border: "1px solid #1e1e2e", borderRadius: 8, padding: "10px 14px", color: "#e0e0f0", fontSize: 14, outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
        onFocus={e => e.target.style.borderColor = "#7c3aed"}
        onBlur={e => e.target.style.borderColor = "#1e1e2e"}
      />
    </div>
  );
}

// ─── Login Gate ──────────────────────────────────────────────────────────────
function LoginGate({ onLogin }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) return;
    setLoading(true); setError("");
    const res = await adminFetch("/api/admin/stats", trimmed).catch(() => null);
    setLoading(false);
    if (!res || res.status === 403) {
      setError("Invalid admin key. Please try again.");
    } else {
      sessionStorage.setItem(SESSION_KEY, trimmed);
      onLogin(trimmed);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#08080f", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#0e0e1a", border: "1px solid #1e1e2e", borderRadius: 20, padding: "36px 28px", width: "100%", maxWidth: 380, boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg,#7c3aed,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, margin: "0 auto 16px", boxShadow: "0 8px 24px rgba(124,58,237,0.4)" }}>🛡️</div>
          <h1 style={{ color: "#f0f0ff", fontSize: 22, fontWeight: 700, margin: 0 }}>hirrd Admin</h1>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "8px 0 0" }}>Enter your admin key to continue</p>
        </div>
        <form onSubmit={submit}>
          <input
            type="password" value={key} onChange={e => setKey(e.target.value)}
            placeholder="Admin key"
            autoCapitalize="none" autoCorrect="off" autoComplete="current-password" spellCheck={false}
            style={{ width: "100%", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 10, padding: "12px 16px", color: "#e0e0f0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
            onFocus={e => e.target.style.borderColor = "#7c3aed"}
            onBlur={e => e.target.style.borderColor = "#1e1e2e"}
          />
          {error && (
            <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, color: "#f87171", fontSize: 13 }}>{error}</div>
          )}
          <button type="submit" disabled={loading} style={{ marginTop: 14, width: "100%", padding: "12px", background: loading ? "#3b2f63" : "linear-gradient(135deg,#7c3aed,#a855f7)", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 16px rgba(124,58,237,0.35)" }}>
            {loading ? "Verifying..." : "Access Dashboard →"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────
function DashboardTab({ adminKey }) {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminFetch("/api/admin/stats", adminKey).then(r => r.json()),
      adminFetch("/api/admin/contacts", adminKey).then(r => r.json()),
    ]).then(([s, c]) => {
      setStats(s); setContacts((c.contacts || []).slice(0, 6));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [adminKey]);

  if (loading) return <Spinner />;

  return (
    <div>
      <h2 style={sectionTitle}>Overview</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 36 }}>
        <StatCard icon="👥" label="Total Users" value={stats?.total_users ?? 0} color="#8b5cf6" />
        <StatCard icon="🧑‍💻" label="Engineers" value={stats?.total_engineers ?? 0} color="#06b6d4" />
        <StatCard icon="🏢" label="Companies" value={stats?.total_companies ?? 0} color="#f59e0b" />
        <StatCard icon="✉️" label="Contacts" value={stats?.total_contacts ?? 0} color="#22c55e" />
      </div>
      <h3 style={{ color: "#9ca3af", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Recent Contact Submissions</h3>
      {contacts.length === 0 ? <EmptyState icon="📭" text="No contact submissions yet." /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contacts.map(c => (
            <div key={c.id} style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 12, padding: "12px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                <div>
                  <span style={{ color: "#e0e0f0", fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                  <span style={{ color: "#6b7280", fontSize: 13, marginLeft: 8 }}>{c.email}</span>
                  {c.company && <span style={{ color: "#7c3aed", fontSize: 12, marginLeft: 6 }}>@ {c.company}</span>}
                </div>
                <div style={{ color: "#4b5563", fontSize: 12, flexShrink: 0 }}>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Engineers Tab ────────────────────────────────────────────────────────────
const blankEngineer = { email: "", password: "", full_name: "", desired_role: "", years_experience: "", tech_stack: "", salary_expectation: "", linkedin_url: "", github_url: "", bio: "" };

function EngineersTab({ adminKey }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(blankEngineer);
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    adminFetch("/api/admin/engineers", adminKey).then(r => r.json())
      .then(d => { setData(d.engineers || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [adminKey]);

  const filtered = data.filter(e => {
    const q = search.toLowerCase();
    return (e.full_name || "").toLowerCase().includes(q)
      || (e.email || "").toLowerCase().includes(q)
      || (e.desired_role || "").toLowerCase().includes(q)
      || (Array.isArray(e.tech_stack) ? e.tech_stack.join(" ").toLowerCase().includes(q) : false);
  });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const res = await adminDelete(`/api/admin/engineers/${confirmDelete.id}`, adminKey).catch(() => null);
    setDeleting(false);
    if (res?.ok || res?.status === 204) {
      setData(d => d.filter(e => e.id !== confirmDelete.id));
      setConfirmDelete(null);
    } else { alert("Delete failed. Please try again."); }
  };

  const f = k => v => setAddForm(p => ({ ...p, [k]: v }));

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddError("");
    const res = await adminPost("/api/admin/engineers", adminKey, {
      ...addForm,
      years_experience: addForm.years_experience ? parseInt(addForm.years_experience, 10) : null,
    }).catch(() => null);
    setAddLoading(false);
    if (!res) { setAddError("Network error."); return; }
    if (res.ok) {
      const newEng = await res.json();
      setData(d => [newEng, ...d]);
      setShowAdd(false); setAddForm(blankEngineer);
    } else {
      const err = await res.json().catch(() => ({}));
      setAddError(err.detail || "Failed to create engineer.");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", marginBottom: 20, gap: 12 }}>
        <h2 style={sectionTitle}>Engineers <span style={{ color: "#7c3aed", fontSize: 16 }}>({filtered.length})</span></h2>
        <div style={{ display: "flex", gap: 10, width: isMobile ? "100%" : "auto", flexWrap: "wrap" }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search name, email, role…" />
          <button onClick={() => setShowAdd(true)} style={addBtnStyle}>+ Add Engineer</button>
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState icon="🧑‍💻" text="No engineers found." /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(e => (
            <div key={e.id} style={{ background: "#111118", border: `1px solid ${expanded === e.id ? "#7c3aed" : "#1e1e2e"}`, borderRadius: 14, overflow: "hidden", transition: "border-color 0.2s" }}>
              <div onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#7c3aed,#06b6d4)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "white", fontSize: 14, flexShrink: 0 }}>
                  {(e.full_name || "?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#e0e0f0", fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.full_name || "—"}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.email || "—"}</div>
                </div>
                {!isMobile && <div style={{ color: "#9ca3af", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}>{e.desired_role || "No role"}</div>}
                {!isMobile && <div style={{ color: "#7c3aed", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}>{e.years_experience != null ? `${e.years_experience} yrs` : "—"}</div>}
                {!isMobile && e.cv_file_path && (
                  <a href={`${API}/uploads/${e.cv_file_path.split("/").pop()}`} target="_blank" rel="noopener noreferrer" onClick={ev => ev.stopPropagation()} style={{ background: "rgba(139,92,246,0.15)", color: "#a78bfa", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, textDecoration: "none", flexShrink: 0 }}>⬇ CV</a>
                )}
                <button onClick={ev => { ev.stopPropagation(); setConfirmDelete({ id: e.id, name: e.full_name || e.email }); }} style={deleteBtnStyle} title="Delete">🗑</button>
                <div style={{ color: "#4b5563", fontSize: 15, flexShrink: 0 }}>{expanded === e.id ? "▲" : "▼"}</div>
              </div>

              {expanded === e.id && (
                <div style={{ borderTop: "1px solid #1e1e2e", padding: "16px 18px", background: "#0d0d16" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 14 }}>
                    <InfoField label="Email" value={e.email} />
                    <InfoField label="Auth Provider" value={e.auth_provider} />
                    <InfoField label="Salary" value={e.salary_expectation} />
                    <InfoField label="Joined" value={e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"} />
                    {isMobile && <InfoField label="Role" value={e.desired_role} />}
                    {isMobile && <InfoField label="Experience" value={e.years_experience != null ? `${e.years_experience} yrs` : "—"} />}
                  </div>
                  {e.bio && <div style={{ marginBottom: 14 }}><InfoField label="Bio" value={e.bio} /></div>}
                  {Array.isArray(e.tech_stack) && e.tech_stack.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Tech Stack</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {e.tech_stack.map(t => <Tag key={t} text={t} />)}
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {e.linkedin_url && <ExtLink href={e.linkedin_url} label="LinkedIn" />}
                    {e.github_url && <ExtLink href={e.github_url} label="GitHub" />}
                    {e.cv_file_path && <a href={`${API}/uploads/${e.cv_file_path.split("/").pop()}`} target="_blank" rel="noopener noreferrer" style={linkStyle}>📄 Download CV</a>}
                    <button onClick={() => setConfirmDelete({ id: e.id, name: e.full_name || e.email })} style={{ ...deleteBtnStyle, padding: "6px 14px", fontSize: 13 }}>🗑 Delete Engineer</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDelete && <ConfirmModal name={confirmDelete.name} onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} loading={deleting} />}

      {showAdd && (
        <FormModal title="Add Engineer" icon="🧑‍💻" onClose={() => { setShowAdd(false); setAddForm(blankEngineer); setAddError(""); }} onSubmit={handleAdd} loading={addLoading} error={addError}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <FInput label="Email" required value={addForm.email} onChange={f("email")} placeholder="engineer@email.com" />
            <FInput label="Password" type="password" value={addForm.password} onChange={f("password")} placeholder="Initial password" />
          </div>
          <FInput label="Full Name" required value={addForm.full_name} onChange={f("full_name")} placeholder="Jane Smith" />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <FInput label="Desired Role" value={addForm.desired_role} onChange={f("desired_role")} placeholder="Full-Stack Engineer" />
            <FInput label="Years Experience" type="number" value={addForm.years_experience} onChange={f("years_experience")} placeholder="3" />
          </div>
          <FInput label="Tech Stack" value={addForm.tech_stack} onChange={f("tech_stack")} placeholder="React, Node.js, Python (comma-separated)" />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <FInput label="Salary Expectation" value={addForm.salary_expectation} onChange={f("salary_expectation")} placeholder="$5000/mo" />
            <FInput label="LinkedIn URL" value={addForm.linkedin_url} onChange={f("linkedin_url")} placeholder="https://linkedin.com/in/…" />
          </div>
          <FInput label="GitHub URL" value={addForm.github_url} onChange={f("github_url")} placeholder="https://github.com/…" />
          <FTextarea label="Bio" value={addForm.bio} onChange={f("bio")} placeholder="Brief bio…" />
        </FormModal>
      )}
    </div>
  );
}

// ─── Companies Tab ────────────────────────────────────────────────────────────
const blankCompany = { email: "", company_name: "", industry: "", company_size: "", website: "", contact_person: "" };

function CompaniesTab({ adminKey }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(blankCompany);
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    adminFetch("/api/admin/companies", adminKey).then(r => r.json())
      .then(d => { setData(d.companies || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [adminKey]);

  const filtered = data.filter(c => {
    const q = search.toLowerCase();
    return (c.company_name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (c.industry || "").toLowerCase().includes(q)
      || (c.contact_person || "").toLowerCase().includes(q);
  });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const res = await adminDelete(`/api/admin/companies/${confirmDelete.id}`, adminKey).catch(() => null);
    setDeleting(false);
    if (res?.ok || res?.status === 204) {
      setData(d => d.filter(c => c.id !== confirmDelete.id));
      setConfirmDelete(null);
    } else { alert("Delete failed. Please try again."); }
  };

  const f = k => v => setAddForm(p => ({ ...p, [k]: v }));

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddError("");
    const res = await adminPost("/api/admin/companies", adminKey, addForm).catch(() => null);
    setAddLoading(false);
    if (!res) { setAddError("Network error."); return; }
    if (res.ok) {
      const newComp = await res.json();
      setData(d => [newComp, ...d]);
      setShowAdd(false); setAddForm(blankCompany);
    } else {
      const err = await res.json().catch(() => ({}));
      setAddError(err.detail || "Failed to create company.");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", marginBottom: 20, gap: 12 }}>
        <h2 style={sectionTitle}>Companies <span style={{ color: "#f59e0b", fontSize: 16 }}>({filtered.length})</span></h2>
        <div style={{ display: "flex", gap: 10, width: isMobile ? "100%" : "auto", flexWrap: "wrap" }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search name, email, industry…" />
          <button onClick={() => setShowAdd(true)} style={addBtnStyle}>+ Add Company</button>
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState icon="🏢" text="No companies found." /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(c => (
            <div key={c.id} style={{ background: "#111118", border: `1px solid ${expanded === c.id ? "#f59e0b" : "#1e1e2e"}`, borderRadius: 14, overflow: "hidden", transition: "border-color 0.2s" }}>
              <div onClick={() => setExpanded(expanded === c.id ? null : c.id)} style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#f59e0b,#ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "white", fontSize: 14, flexShrink: 0 }}>
                  {(c.company_name || "?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#e0e0f0", fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.company_name || "—"}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email || "—"}</div>
                </div>
                {!isMobile && <div style={{ color: "#9ca3af", fontSize: 13, flexShrink: 0 }}>{c.industry || "—"}</div>}
                {!isMobile && <div style={{ color: "#f59e0b", fontSize: 13, flexShrink: 0 }}>{c.company_size || "—"}</div>}
                <button onClick={ev => { ev.stopPropagation(); setConfirmDelete({ id: c.id, name: c.company_name || c.email }); }} style={deleteBtnStyle} title="Delete">🗑</button>
                <div style={{ color: "#4b5563", fontSize: 15, flexShrink: 0 }}>{expanded === c.id ? "▲" : "▼"}</div>
              </div>
              {expanded === c.id && (
                <div style={{ borderTop: "1px solid #1e1e2e", padding: "16px 18px", background: "#0d0d16" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 14 }}>
                    <InfoField label="Contact Person" value={c.contact_person} />
                    <InfoField label="Website" value={c.website} isLink />
                    <InfoField label="Auth Provider" value={c.auth_provider} />
                    <InfoField label="Joined" value={c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"} />
                    {isMobile && <InfoField label="Industry" value={c.industry} />}
                    {isMobile && <InfoField label="Size" value={c.company_size} />}
                  </div>
                  <button onClick={() => setConfirmDelete({ id: c.id, name: c.company_name || c.email })} style={{ ...deleteBtnStyle, padding: "6px 14px", fontSize: 13 }}>🗑 Delete Company</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDelete && <ConfirmModal name={confirmDelete.name} onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} loading={deleting} />}

      {showAdd && (
        <FormModal title="Add Company" icon="🏢" onClose={() => { setShowAdd(false); setAddForm(blankCompany); setAddError(""); }} onSubmit={handleAdd} loading={addLoading} error={addError}>
          <FInput label="Company Email" required value={addForm.email} onChange={f("email")} placeholder="company@example.com" />
          <FInput label="Company Name" required value={addForm.company_name} onChange={f("company_name")} placeholder="Acme Corp" />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <FInput label="Industry" value={addForm.industry} onChange={f("industry")} placeholder="SaaS" />
            <FInput label="Company Size" value={addForm.company_size} onChange={f("company_size")} placeholder="11-50" />
          </div>
          <FInput label="Website" value={addForm.website} onChange={f("website")} placeholder="https://company.com" />
          <FInput label="Contact Person" value={addForm.contact_person} onChange={f("contact_person")} placeholder="John Smith" />
        </FormModal>
      )}
    </div>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────
const blankContact = { name: "", email: "", company: "", message: "" };

function ContactsTab({ adminKey }) {
  const isMobile = useIsMobile();
  const [contacts, setContacts] = useState([]);
  const [readSet, setReadSet] = useState(getReadSet());
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(blankContact);
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    adminFetch("/api/admin/contacts", adminKey).then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [adminKey]);

  const open = (c) => { markRead(c.id); setReadSet(getReadSet()); setSelected(c); };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const res = await adminDelete(`/api/admin/contacts/${confirmDelete.id}`, adminKey).catch(() => null);
    setDeleting(false);
    if (res?.ok || res?.status === 204) {
      setContacts(d => d.filter(c => c.id !== confirmDelete.id));
      setSelected(null); setConfirmDelete(null);
    } else { alert("Delete failed. Please try again."); }
  };

  const f = k => v => setAddForm(p => ({ ...p, [k]: v }));

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddError("");
    const res = await adminPost("/api/admin/contacts", adminKey, addForm).catch(() => null);
    setAddLoading(false);
    if (!res) { setAddError("Network error."); return; }
    if (res.ok) {
      const nc = await res.json();
      setContacts(d => [nc, ...d]);
      setShowAdd(false); setAddForm(blankContact);
    } else {
      const err = await res.json().catch(() => ({}));
      setAddError(err.detail || "Failed to create contact.");
    }
  };

  if (loading) return <Spinner />;
  const unread = contacts.filter(c => !readSet.has(c.id)).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <h2 style={sectionTitle}>Contacts Inbox</h2>
        {unread > 0 && <span style={{ background: "#7c3aed", color: "white", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{unread} unread</span>}
        <div style={{ marginLeft: "auto" }}>
          <button onClick={() => setShowAdd(true)} style={addBtnStyle}>+ Add Contact</button>
        </div>
      </div>

      {contacts.length === 0 ? <EmptyState icon="📭" text="No contact submissions yet." /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {contacts.map(c => {
            const isRead = readSet.has(c.id);
            return (
              <div key={c.id} style={{ background: isRead ? "#111118" : "#13131f", border: `1px solid ${isRead ? "#1e1e2e" : "#2d2047"}`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: isRead ? "#1e1e2e" : "#7c3aed", flexShrink: 0 }} />
                <div
                  onClick={() => open(c)}
                  style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.parentElement.style.borderColor = "#7c3aed"; }}
                  onMouseLeave={e => { e.currentTarget.parentElement.style.borderColor = isRead ? "#1e1e2e" : "#2d2047"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: isRead ? "#9ca3af" : "#e0e0f0", fontWeight: isRead ? 500 : 700, fontSize: 14 }}>{c.name}</span>
                    {c.company && <span style={{ color: "#7c3aed", fontSize: 12 }}>@ {c.company}</span>}
                  </div>
                  <div style={{ color: "#4b5563", fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.message}</div>
                </div>
                {!isMobile && <div style={{ color: "#4b5563", fontSize: 12, flexShrink: 0 }}>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : "—"}</div>}
                <button onClick={ev => { ev.stopPropagation(); setConfirmDelete({ id: c.id, name: c.name }); }} style={deleteBtnStyle} title="Delete">🗑</button>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "16px", overflowY: "auto" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#111118", border: "1px solid #2a2a40", borderRadius: 20, padding: "24px 20px", maxWidth: 560, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.6)", margin: "20px auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#e0e0f0", fontWeight: 700, fontSize: 18 }}>{selected.name}</div>
                <div style={{ color: "#7c3aed", fontSize: 13, marginTop: 4 }}>{selected.email}</div>
                {selected.company && <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>{selected.company}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexShrink: 0 }}>
                <button onClick={() => setConfirmDelete({ id: selected.id, name: selected.name })} style={{ ...deleteBtnStyle, padding: "6px 12px", fontSize: 12 }}>🗑 Delete</button>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 22, cursor: "pointer" }}>✕</button>
              </div>
            </div>
            <div style={{ color: "#9ca3af", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Message</div>
            <div style={{ color: "#d1d5db", fontSize: 15, lineHeight: 1.7, background: "#0d0d16", borderRadius: 12, padding: "14px 16px", border: "1px solid #1e1e2e", whiteSpace: "pre-wrap" }}>{selected.message}</div>
            <div style={{ color: "#4b5563", fontSize: 12, marginTop: 14 }}>{selected.submitted_at ? new Date(selected.submitted_at).toLocaleString() : "—"}</div>
          </div>
        </div>
      )}

      {confirmDelete && <ConfirmModal name={confirmDelete.name} onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} loading={deleting} />}

      {showAdd && (
        <FormModal title="Add Contact" icon="✉️" onClose={() => { setShowAdd(false); setAddForm(blankContact); setAddError(""); }} onSubmit={handleAdd} loading={addLoading} error={addError}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <FInput label="Name" required value={addForm.name} onChange={f("name")} placeholder="Jane Smith" />
            <FInput label="Email" required value={addForm.email} onChange={f("email")} placeholder="jane@company.com" />
          </div>
          <FInput label="Company" value={addForm.company} onChange={f("company")} placeholder="Acme Corp (optional)" />
          <FTextarea label="Message" required value={addForm.message} onChange={f("message")} placeholder="Message content…" rows={4} />
        </FormModal>
      )}
    </div>
  );
}

// ─── Matches Tab ──────────────────────────────────────────────────────────────
function MatchesTab({ adminKey }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    adminFetch("/api/admin/matches", adminKey).then(r => r.json())
      .then(d => {
        if (d.matches) setData(d.matches);
        else setError("No match data available.");
        setLoading(false);
      })
      .catch(() => { setError("Could not load matches."); setLoading(false); });
  }, [adminKey]);

  const filtered = data.filter(m => {
    const q = search.toLowerCase();
    return (m.company_name || "").toLowerCase().includes(q)
      || (m.engineer_name || "").toLowerCase().includes(q)
      || (m.desired_role || "").toLowerCase().includes(q);
  });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const res = await adminDelete(`/api/admin/matches/${confirmDelete.id}`, adminKey).catch(() => null);
    setDeleting(false);
    if (res?.ok || res?.status === 204) {
      setData(d => d.filter(m => m.id !== confirmDelete.id));
      setConfirmDelete(null);
    } else { alert("Delete failed. Please try again."); }
  };

  if (loading) return <Spinner />;
  if (error) return <EmptyState icon="🤖" text={error} />;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", marginBottom: 20, gap: 12 }}>
        <h2 style={sectionTitle}>AI Match Results <span style={{ color: "#22c55e", fontSize: 16 }}>({filtered.length})</span></h2>
        <SearchBar value={search} onChange={setSearch} placeholder="Search company, engineer, role…" />
      </div>

      {filtered.length === 0 ? <EmptyState icon="🤖" text="No AI matches run yet." /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(m => (
            <div key={m.id} style={{ background: "#111118", border: `1px solid ${expanded === m.id ? "#22c55e" : "#1e1e2e"}`, borderRadius: 14, overflow: "hidden", transition: "border-color 0.2s" }}>
              <div onClick={() => setExpanded(expanded === m.id ? null : m.id)} style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#e0e0f0", fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.engineer_name || "—"}</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>for <span style={{ color: "#a78bfa" }}>{m.company_name}</span></div>
                </div>
                <div style={{ display: "flex", gap: isMobile ? 6 : 14, flexShrink: 0 }}>
                  <ScoreBadge value={m.overall_score} label="Overall" />
                  {!isMobile && <ScoreBadge value={m.fit_score} label="Fit" />}
                  {!isMobile && <ScoreBadge value={m.retention_score} label="Retention" />}
                </div>
                <button onClick={ev => { ev.stopPropagation(); setConfirmDelete({ id: m.id, name: `${m.engineer_name} ↔ ${m.company_name}` }); }} style={deleteBtnStyle} title="Delete">🗑</button>
                <div style={{ color: "#4b5563", fontSize: 15, flexShrink: 0 }}>{expanded === m.id ? "▲" : "▼"}</div>
              </div>

              {expanded === m.id && (
                <div style={{ borderTop: "1px solid #1e1e2e", padding: "16px 18px", background: "#0d0d16" }}>
                  {isMobile && (
                    <div style={{ display: "flex", gap: 20, marginBottom: 16, justifyContent: "center" }}>
                      <ScoreBadge value={m.fit_score} label="Fit" />
                      <ScoreBadge value={m.retention_score} label="Retention" />
                    </div>
                  )}
                  {m.desired_role && <div style={{ marginBottom: 12 }}><InfoField label="Desired Role" value={m.desired_role} /></div>}
                  {Array.isArray(m.tech_stack) && m.tech_stack.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Tech Stack</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {m.tech_stack.map(t => <Tag key={t} text={t} />)}
                      </div>
                    </div>
                  )}
                  {m.ai_rationale && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>AI Rationale</div>
                      <div style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1.7, background: "#111118", borderRadius: 10, padding: "12px 14px", border: "1px solid #1e1e2e" }}>{m.ai_rationale}</div>
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                    {Array.isArray(m.strengths) && m.strengths.length > 0 && (
                      <div>
                        <div style={{ color: "#22c55e", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>✓ Strengths</div>
                        {m.strengths.map((s, i) => <div key={i} style={{ color: "#9ca3af", fontSize: 13, marginBottom: 4 }}>• {s}</div>)}
                      </div>
                    )}
                    {Array.isArray(m.concerns) && m.concerns.length > 0 && (
                      <div>
                        <div style={{ color: "#f87171", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>⚠ Concerns</div>
                        {m.concerns.map((c, i) => <div key={i} style={{ color: "#9ca3af", fontSize: 13, marginBottom: 4 }}>• {c}</div>)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
                    {m.created_at && <div style={{ color: "#4b5563", fontSize: 12 }}>Matched on {new Date(m.created_at).toLocaleString()}</div>}
                    <button onClick={() => setConfirmDelete({ id: m.id, name: `${m.engineer_name} ↔ ${m.company_name}` })} style={{ ...deleteBtnStyle, padding: "6px 14px", fontSize: 13 }}>🗑 Delete Match</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDelete && <ConfirmModal name={confirmDelete.name} onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} loading={deleting} />}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const linkStyle = {
  background: "rgba(124,58,237,0.12)", color: "#a78bfa",
  borderRadius: 8, padding: "6px 12px", fontSize: 13,
  fontWeight: 600, textDecoration: "none", border: "1px solid rgba(124,58,237,0.2)",
};
const deleteBtnStyle = {
  background: "rgba(239,68,68,0.1)", color: "#f87171",
  border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8,
  padding: "5px 10px", fontSize: 13, cursor: "pointer", flexShrink: 0,
};
const addBtnStyle = {
  background: "linear-gradient(135deg,#7c3aed,#a855f7)", color: "white",
  border: "none", borderRadius: 10, padding: "9px 16px",
  fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
};
const sectionTitle = { color: "#e0e0f0", fontSize: 20, fontWeight: 700, margin: 0 };

// ─── Nav Item ─────────────────────────────────────────────────────────────────
function NavItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ width: "100%", background: active ? "rgba(124,58,237,0.18)" : "none", border: `1px solid ${active ? "rgba(124,58,237,0.35)" : "transparent"}`, borderRadius: 10, padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, color: active ? "#c4b5fd" : "#6b7280", fontWeight: active ? 600 : 500, fontSize: 14, transition: "all 0.2s", textAlign: "left" }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "rgba(124,58,237,0.08)"; e.currentTarget.style.color = "#9ca3af"; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#6b7280"; } }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ─── Main Admin Shell ─────────────────────────────────────────────────────────
const NAV = [
  { id: "dashboard", icon: "📊", label: "Dashboard" },
  { id: "engineers", icon: "🧑‍💻", label: "Engineers" },
  { id: "companies", icon: "🏢", label: "Companies" },
  { id: "contacts", icon: "✉️", label: "Contacts" },
  { id: "matches", icon: "🤖", label: "AI Matches" },
];

export default function AdminDashboard() {
  const isMobile = useIsMobile();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [tab, setTab] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => { sessionStorage.removeItem(SESSION_KEY); setAdminKey(""); };

  if (!adminKey) return <LoginGate onLogin={setAdminKey} />;

  const currentNav = NAV.find(n => n.id === tab);

  const sidebarInner = (
    <>
      <div style={{ padding: "0 8px 24px", borderBottom: "1px solid #151525", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#7c3aed,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: "0 4px 12px rgba(124,58,237,0.4)" }}>🛡️</div>
          <div>
            <div style={{ color: "#e0e0f0", fontWeight: 700, fontSize: 15 }}>hirrd</div>
            <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Admin</div>
          </div>
        </div>
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        {NAV.map(n => (
          <NavItem key={n.id} {...n} active={tab === n.id} onClick={() => { setTab(n.id); setDrawerOpen(false); }} />
        ))}
      </nav>
      <button
        onClick={handleLogout}
        style={{ marginTop: 16, background: "none", border: "1px solid #1e1e2e", borderRadius: 10, padding: "10px 14px", cursor: "pointer", color: "#4b5563", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 10, transition: "all 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.3)"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#4b5563"; e.currentTarget.style.borderColor = "#1e1e2e"; }}
      >
        <span>🚪</span> Logout
      </button>
    </>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'Google Sans',system-ui,sans-serif", background: "#08080f" }}>

      {/* Desktop sidebar */}
      {!isMobile && (
        <aside style={{ width: 220, background: "#0a0a14", borderRight: "1px solid #151525", display: "flex", flexDirection: "column", padding: "24px 12px", flexShrink: 0, position: "sticky", top: 0, height: "100vh", overflowY: "auto" }}>
          {sidebarInner}
        </aside>
      )}

      {/* Mobile drawer backdrop */}
      {isMobile && drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200 }} />
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <aside style={{ position: "fixed", top: 0, left: 0, height: "100vh", width: 240, background: "#0a0a14", borderRight: "1px solid #151525", display: "flex", flexDirection: "column", padding: "24px 12px", zIndex: 250, transform: drawerOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.28s ease", overflowY: "auto" }}>
          {sidebarInner}
        </aside>
      )}

      {/* Content area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflowX: "hidden" }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{ position: "sticky", top: 0, zIndex: 100, background: "#0a0a14", borderBottom: "1px solid #151525", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setDrawerOpen(o => !o)} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 22, cursor: "pointer", padding: 4, flexShrink: 0, lineHeight: 1 }}>☰</button>
            <span style={{ fontSize: 18 }}>{currentNav?.icon}</span>
            <span style={{ color: "#e0e0f0", fontWeight: 600, fontSize: 16, flex: 1 }}>{currentNav?.label}</span>
            <button onClick={handleLogout} style={{ background: "none", border: "1px solid #1e1e2e", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#4b5563", fontSize: 13 }}>🚪</button>
          </div>
        )}

        <main style={{ flex: 1, padding: isMobile ? "18px 14px" : "36px 40px", overflowY: "auto" }}>
          {tab === "dashboard" && <DashboardTab adminKey={adminKey} />}
          {tab === "engineers" && <EngineersTab adminKey={adminKey} />}
          {tab === "companies" && <CompaniesTab adminKey={adminKey} />}
          {tab === "contacts" && <ContactsTab adminKey={adminKey} />}
          {tab === "matches" && <MatchesTab adminKey={adminKey} />}
        </main>
      </div>
    </div>
  );
}
